import { t } from "i18next";
import { GetFacilityForm as GetFacilityFormFacility } from "api/rpc/2024-04/facilityAdmin/facility/form/form";
import { GetRegisters } from "api/rpc/2024-04/facilityAdmin/facility/register/register";
import { GetFacilityForm as GetFacilityFormClient } from "api/rpc/2024-04/clientAdmin/client/form";
import { GetFacility } from "api/rpc/clientAdmin/facility/facility";
import { IRegister } from "pages/secure/facility/Admin";
import { StatusCode } from "api/protocols";
import { TReportAdminScope } from "../reportCatalog.types";
import { getQuickExportTemplateId } from "./quickExportConfig";
import { TQuickExportParamsResult } from "./quickExport.types";

interface IEnrichQuickExportParamsOptions {
  needsFacilityId?: boolean;
  isDedicatedRoute?: boolean;
}

async function enrichCashReconciliationParams(
  code: string,
  params: Record<string, unknown>,
): Promise<TQuickExportParamsResult> {
  const registerRes = await GetRegisters({}, false);

  if (registerRes.status !== StatusCode.OK || !registerRes.data?.length) {
    return { ok: false, errorMessage: t("No registers found") };
  }

  const registerIds = registerRes.data.map((register: IRegister) => register.id);

  return {
    ok: true,
    params: {
      ...params,
      register_ids: registerIds,
      ...(code === "cash-reconciliation" ? { sales_channels: [] } : {}),
    },
  };
}

async function enrichFormResponsesParams(
  scope: TReportAdminScope,
  params: Record<string, unknown>,
): Promise<TQuickExportParamsResult> {
  const formsRes =
    scope === "facility"
      ? await GetFacilityFormFacility({}, false)
      : await GetFacilityFormClient({}, false);

  if (formsRes.status !== StatusCode.OK || !formsRes.data?.length) {
    return { ok: false, errorMessage: t("No forms found") };
  }

  const firstForm = formsRes.data[0];

  return {
    ok: true,
    params: {
      ...params,
      form_id: firstForm.id,
    },
  };
}

async function enrichClientFacilityIdParams(
  params: Record<string, unknown>,
): Promise<TQuickExportParamsResult> {
  const facilityRes = await GetFacility({}, false);

  if (facilityRes.status !== StatusCode.OK || !facilityRes.data?.length) {
    return { ok: false, errorMessage: t("No facilities found") };
  }

  const firstFacility = facilityRes.data[0];

  return {
    ok: true,
    params: {
      ...params,
      facility_id: firstFacility.id,
    },
  };
}

function needsClientFacilityId(
  scope: TReportAdminScope,
  code: string,
  params: Record<string, unknown>,
  options?: IEnrichQuickExportParamsOptions,
): boolean {
  if (scope !== "client" || params.facility_id != null) {
    return false;
  }

  if (options?.needsFacilityId) {
    return true;
  }

  return options?.isDedicatedRoute === true && getQuickExportTemplateId(scope, code) === "clientTeeSheetUtilization";
}

export async function enrichQuickExportParams(
  scope: TReportAdminScope,
  code: string,
  params: Record<string, unknown>,
  options?: IEnrichQuickExportParamsOptions,
): Promise<TQuickExportParamsResult> {
  if (code === "cash-reconciliation" || code === "cash-reconciliation-worksheet") {
    return enrichCashReconciliationParams(code, params);
  }

  if (code === "form-responses") {
    return enrichFormResponsesParams(scope, params);
  }

  if (needsClientFacilityId(scope, code, params, options)) {
    return enrichClientFacilityIdParams(params);
  }

  return { ok: true, params };
}
